// frontend/app/components/lessonPlans/blocks/LessonTextBlocks.js
//
// Prose and guidance blocks: rich_text, bullet_list, callout, teacher_tip and
// the "Investigate with Saras AI" card.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getCalloutVariant } from "../lessonPlanTheme";
import { BlockBody, BlockCard, BlockHeading } from "./LessonBlockShell";

export const RichTextBlock = ({ block, colors }) => (
  <BlockCard colors={colors}>
    <BlockHeading colors={colors} title={block.title} />
    <BlockBody colors={colors}>{block.text}</BlockBody>
  </BlockCard>
);

export const BulletListBlock = ({ block, colors }) => {
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <BlockCard colors={colors}>
      <BlockHeading colors={colors} title={block.title} icon="format-list-bulleted" />
      {items.map((item, index) => (
        <View key={`${block.id}-item-${index}`} style={styles.bulletRow}>
          <View style={[styles.bulletDot, { backgroundColor: colors.accentPrimary }]} />
          <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
            {item}
          </Text>
        </View>
      ))}
    </BlockCard>
  );
};

export const CalloutBlock = ({ block, colors, isDarkMode }) => {
  const variant = getCalloutVariant(block.variant, colors, isDarkMode);
  return (
    <View
      style={[
        styles.callout,
        { backgroundColor: variant.bg, borderColor: variant.border },
      ]}
    >
      <MaterialCommunityIcons
        name={variant.icon}
        size={20}
        color={variant.accent}
        style={styles.calloutIcon}
      />
      <View style={styles.calloutBody}>
        {block.title ? (
          <Text style={[styles.calloutTitle, { color: variant.accent }]}>
            {block.title}
          </Text>
        ) : null}
        <Text style={[styles.calloutText, { color: colors.textPrimary }]}>
          {block.text}
        </Text>
      </View>
    </View>
  );
};

/** Teacher-only. The backend strips these in student mode; this is defence in
 *  depth for a payload that somehow still carries one. */
export const TeacherTipBlock = ({ block, colors, mode, t }) => {
  if (mode === "student") return null;
  const translate = t || ((value) => value);

  return (
    <View
      style={[
        styles.callout,
        {
          backgroundColor: colors.teacherTipBg,
          borderColor: colors.teacherTipBorder,
        },
      ]}
    >
      <MaterialCommunityIcons
        name="lightbulb-on-outline"
        size={20}
        color={colors.warning}
        style={styles.calloutIcon}
      />
      <View style={styles.calloutBody}>
        <Text style={[styles.calloutTitle, { color: colors.teacherTipText }]}>
          {translate("Teacher tip")}
        </Text>
        <Text style={[styles.calloutText, { color: colors.textPrimary }]}>
          {block.text}
        </Text>
      </View>
    </View>
  );
};

export const AiPromptBlock = ({ block, colors, onAsk, t }) => {
  const translate = t || ((value) => value);
  const questions = Array.isArray(block.questions) ? block.questions : [];

  return (
    <View
      style={[
        styles.aiCard,
        { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
      ]}
    >
      <View style={styles.aiHeader}>
        <MaterialCommunityIcons
          name="robot-happy-outline"
          size={18}
          color={colors.accentPrimary}
        />
        <Text style={[styles.aiTitle, { color: colors.accentPrimary }]}>
          {block.title || translate("Investigate with Saras AI")}
        </Text>
      </View>

      {questions.map((question, index) => (
        <TouchableOpacity
          key={`${block.id}-q-${index}`}
          onPress={() => onAsk && onAsk(question)}
          disabled={!onAsk}
          accessibilityRole="button"
          accessibilityLabel={question}
          style={[
            styles.aiQuestion,
            { backgroundColor: colors.panelBg, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.aiQuestionText, { color: colors.textPrimary }]}>
            {question}
          </Text>
          <MaterialCommunityIcons
            name="arrow-right"
            size={16}
            color={colors.accentPrimary}
          />
        </TouchableOpacity>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  bulletRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 8 },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 8,
    marginRight: 10,
  },
  bulletText: { fontSize: 14, lineHeight: 22, flex: 1 },

  callout: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    marginBottom: 12,
  },
  calloutIcon: { marginRight: 10, marginTop: 1 },
  calloutBody: { flex: 1 },
  calloutTitle: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  calloutText: { fontSize: 14, lineHeight: 22 },

  aiCard: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    marginBottom: 12,
  },
  aiHeader: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  aiTitle: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginLeft: 8,
  },
  aiQuestion: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
  },
  aiQuestionText: { fontSize: 13, lineHeight: 20, flex: 1, marginRight: 8 },
});
